import React, { FC, useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Card, Chip, CircularProgress, Container, CssBaseline, Divider, Stack, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../components/Header/header';
import { useConnection } from '../connection/Application';
import { paperCrumbs, readBody } from '../components/Cba/cbaApi';
import usePaperDetail from '../components/Cba/usePaperDetail';
import {
  closedBecause,
  ExamResult,
  formatAttemptDate,
  formatTimeUsed,
  outcomeLabel,
  percentageLine,
  scoreLine,
} from '../components/Cba/resultView';

/**
 * How a sitting went, straight after submitting and again on any later visit.
 *
 * The figures come from the server, which marked the paper. Nothing is worked
 * out here.
 */
const CbaResult: FC = () => {
  const { attemptId } = useParams();
  const connection = useConnection();
  const navigate = useNavigate();

  const [result, setResult] = useState<ExamResult>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchResult = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await connection.get(`cba/attempts/${attemptId}/result`);
      setResult(readBody<ExamResult>(response));
    } catch (e: any) {
      const message = typeof e?.message === 'string' ? e.message : '';
      setError(message !== '' ? message : 'This result could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [attemptId, connection]);

  useEffect(() => {
    fetchResult();
  }, [fetchResult]);

  const outcome = result ? outcomeLabel(result) : undefined;
  const closing = result ? closedBecause(result) : undefined;

  const paperId = result?.paper?.id;
  const paperDetail = usePaperDetail(paperId);
  const crumbs = paperCrumbs(paperDetail, result?.paper?.title ?? '', `/cba/papers/${paperId ?? ''}`, {
    label: 'Result',
    href: `/cba/attempts/${attemptId}/result`,
  });

  return (
    <Container sx={{ minHeight: 'calc(100vh - 185px)' }}>
      <CssBaseline />
      <Header x={{ crumbs }} />

      <Box sx={{ pt: { xs: 12, md: 14 }, pb: 6 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress />
          </Box>
        ) : error !== '' || !result ? (
          <>
            <Alert severity='error'>{error || 'This result could not be loaded.'}</Alert>
            <Button sx={{ mt: 2 }} onClick={() => navigate('/cba')}>
              Back to exam papers
            </Button>
          </>
        ) : (
          <Card sx={{ p: { xs: 3, md: 4 }, borderRadius: 3, boxShadow: '0 2px 12px #0000001A' }}>
            <Typography variant='h4' sx={{ color: '#C44A1E', fontWeight: 500 }}>
              {result.paper?.title ?? 'Exam paper'}
            </Typography>
            <Typography variant='body2' sx={{ color: 'text.secondary', mt: 0.5 }}>
              Attempt {result.attempt.attemptNo} · {formatAttemptDate(result.attempt.submittedAt)}
            </Typography>

            {closing && (
              <Alert severity='info' sx={{ mt: 2 }}>
                {closing}
              </Alert>
            )}

            <Stack
              direction='row'
              spacing={2}
              alignItems='baseline'
              sx={{ mt: 3, flexWrap: 'wrap' }}>
              <Typography variant='h3' sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                {percentageLine(result)}
              </Typography>
              <Typography
                variant='h6'
                sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                {scoreLine(result)} marks
              </Typography>
              {outcome && (
                <Chip
                  label={outcome}
                  color={result.passed ? 'success' : 'default'}
                  sx={{ fontWeight: 600 }}
                />
              )}
            </Stack>

            <Divider sx={{ my: 3 }} />

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(2, 1fr)',
                  sm: 'repeat(3, 1fr)',
                  md: 'repeat(5, 1fr)',
                },
                gap: 2,
              }}>
              {[
                { label: 'Questions', value: `${result.totalQuestions}`, colour: 'inherit' },
                { label: 'Correct', value: `${result.correctCount}`, colour: '#0F5C55' },
                { label: 'Incorrect', value: `${result.incorrectCount}`, colour: '#A32A1E' },
                { label: 'Unanswered', value: `${result.unansweredCount}`, colour: '#8A5A12' },
                {
                  label: 'Time used',
                  value: formatTimeUsed(result.attempt.timeUsedSeconds),
                  colour: 'inherit',
                },
              ].map((fact) => (
                <Box key={fact.label}>
                  <Typography
                    variant='caption'
                    sx={{
                      color: 'text.secondary',
                      textTransform: 'uppercase',
                      letterSpacing: '.08em',
                    }}>
                    {fact.label}
                  </Typography>
                  <Typography variant='h6' sx={{ fontWeight: 600, color: fact.colour }}>
                    {fact.value}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Stack direction='row' spacing={1} sx={{ mt: 4, flexWrap: 'wrap', rowGap: 1 }}>
              <Button
                variant='contained'
                onClick={() => navigate(`/cba/attempts/${attemptId}/review`)}>
                Review my answers
              </Button>
              {result.paper && (
                <Button
                  variant='outlined'
                  onClick={() => navigate(`/cba/papers/${result.paper?.id}`)}>
                  Back to this paper
                </Button>
              )}
              <Button onClick={() => navigate('/cba')}>All exam papers</Button>
            </Stack>
          </Card>
        )}
      </Box>
    </Container>
  );
};

export default CbaResult;
